import React, {useState} from 'react';
import {useDisableCompanyMutation, useEnableCompanyMutation, useGetCompaniesQuery} from '../../store/Api';
import {
    ICommandBarItemProps,
    IconButton,
    Link,
    Toggle
} from '@fluentui/react';
import {useHistory} from 'react-router-dom';
import {ICompany} from '../../models/Company';
import CompanyDetailsModal from './CompanyDetailsModal';
import ImportCompanyDialog from './ImportCompanyDialog';
import CsvImportPanel from './CsvImportPanel';
import {CsvImportType} from '../../store/endpoints/CsvImportEndpoints';
import DisableEnableButton from "./DisableEnableButton";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";
import GridComposer from "../GridComposer/GridComposer";
import {useGetStorefrontsQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {useGetCompanyLinkableFoliosQuery} from '../../store/endpoints/RecordLinksEndpoints';
import { useUrlPanelState } from '../../hooks/useUrlState';

const CompanyManagementDisplay: React.FC = () => {
    const [query, setQuery] = React.useState('');
    const [showDisabled, setShowDisabled] = React.useState(false);
    /*
     * The company details modal opens from the URL (?panel=company&instance=<id>). The modal only
     * needs the id, so this does not depend on the record being on the loaded page - unlike the other
     * converted panels, a link to a company on page 5 of the grid still works.
     */
    const companyPanel = useUrlPanelState('company');
    const [newCompany, setNewCompany] = React.useState(false);
    const [importFolio, setImportFolio] = React.useState<{folioId: string; folioName?: string} | undefined>(undefined);
    const [csvImportType, setCsvImportType] = React.useState<CsvImportType | undefined>(undefined);
    const [disableCompany] = useDisableCompanyMutation();
    const [enableCompany] = useEnableCompanyMutation();
    const storefronts = useGetStorefrontsQuery();
    const linkableFolios = useGetCompanyLinkableFoliosQuery();
    const history = useHistory();
    const [pageSize, setPageSize] = useState<number>(25)
    const [filter, setFilter] = useState<string>()
    const [filterField, setFilterField] = useState<string>()
    const [sortBy, setSortBy] = useState<string>()
    const [sortDirection, setSortDirection] = useState<string>()
    const [page, setPage] = React.useState(1);
    const companies = useGetCompaniesQuery({query: {query, conditions: {page: page, pageSize: pageSize, sortBy: sortBy, sortDirection: sortDirection, searchBy: filterField, searchTerm: filter}}, disabled: showDisabled});

    const getCustomColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                identifier: 'Details',
                label: '',
                type: '',
                index: 0,
                component:() => <IconButton iconProps={{iconName: 'Edit'}}/>,
                onClick: (company: ICompany) => companyPanel.open(company.id),
                required: true,
                effectiveWidth: 50,
            },
            {
                identifier: 'enabled',
                label: 'Enabled',
                type: '',
                index: 1,
                component:(company) => <DisableEnableButton disabled={company.disabled} id={company.id} onDisable={() => disableCompany(company.id)} onEnable={() => enableCompany(company.id)}/>,
                required: true,
                effectiveWidth: 50
            },
            {
                identifier: 'storefront.keyword',
                label: 'Storefront',
                type: 'Text',
                sortable: true,
                filterable: true,
                minimumWidth: 120,
                component: (company: ICompany) => {
                    if (!company.storefront) return <span>-</span>;
                    const storefront = storefronts.data?.items?.find(a => a.id === company.storefront);
                    return (
                        <Link onClick={() => history.push(`/settings/storefront/storefronts/${company.storefront}/dashboard`)}>
                            {storefront?.name ?? company.storefront}
                        </Link>
                    );
                },
                customFilterOptions: () => storefronts.data?.items?.map(a => ({key: a.id ?? '', text: a.name ?? ''})),
                tooltip: 'Sort on Storefront is not alphabetical',
            }
        ];
        return columnDetails
    }

    const importFolios = linkableFolios.data ?? [];
    const additionalCommands: ICommandBarItemProps[] = [
        {
            key: 'new',
            text: 'Add New Company',
            iconProps: { iconName: 'Add' },
            onClick: () => { setNewCompany(true); },
        },
        {
            key: 'import',
            text: 'Import',
            iconProps: { iconName: 'Import' },
            subMenuProps: {
                items: [
                    ...importFolios.map(f => ({
                        key: `crm-${f.folioId}`,
                        text: `From ${f.folioName ?? 'CRM'}`,
                        iconProps: { iconName: 'Download' },
                        onClick: () => setImportFolio({folioId: f.folioId, folioName: f.folioName}),
                    })),
                    {
                        key: 'companiesFromCsv',
                        text: 'Companies from CSV',
                        iconProps: { iconName: 'ExcelDocument' },
                        onClick: () => setCsvImportType('company'),
                    },
                    {
                        key: 'locationsFromCsv',
                        text: 'Locations from CSV',
                        iconProps: { iconName: 'ExcelDocument' },
                        onClick: () => setCsvImportType('location'),
                    },
                ]
            },
        },
        {
            key: 'includeDisabled',
            onRender: () => (
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                    <Toggle
                        label="Include Disabled"
                        inlineLabel
                        checked={showDisabled}
                        styles={{ root: { marginBottom: 0 } }}
                        onChange={(ev, checked) => {
                            setShowDisabled(checked ?? showDisabled);
                            setPage(1);
                        }}
                    />
                </div>
            ),
        },
    ]

    return (
        <div>
            <GridComposer
                data={companies.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={companies.isFetching}
                onRefresh={() => companies.refetch()}
                selectionMode={GridSelectionMode.none}
                columnSetName={'Company'}
                filter={filter}
                filterField={filterField}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(column, direction) => {
                    setSortBy(column)
                    setSortDirection(direction)
                }}
                onFilter={(filter, column) => {
                    setFilter(filter)
                    setFilterField(column)
                }}
                overrideColumns={getCustomColumns()}
                canDragColumns={true}
                canResizeColumns={true}
                canCustomizeColumns={true}
                canChangePageSize={true}
                canCreateCsv={false}
                additionalCommands={additionalCommands}
                getItemId={(company: ICompany) => company.id ?? ''}
            />
            <CompanyDetailsModal
                company={companyPanel.instanceId}
                isOpen={companyPanel.isOpen || newCompany}
                onClose={() => {
                    companyPanel.close();
                    setNewCompany(false);
                }}
                />
            <ImportCompanyDialog
                folioId={importFolio?.folioId}
                folioName={importFolio?.folioName}
                onClose={() => setImportFolio(undefined)}
                onImported={() => companies.refetch()}
                />
            <CsvImportPanel
                importType={csvImportType ?? 'company'}
                isOpen={csvImportType !== undefined}
                onClose={() => setCsvImportType(undefined)}
                onImported={() => companies.refetch()}
                />
        </div>
    );
}

export default CompanyManagementDisplay;